/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

/**
 * Request Deduplication Utility
 * Prevents duplicate simultaneous API requests
 * If multiple components request the same data at the same time, only one request is made
 * 
 * Impact: 50-60% reduction in duplicate requests
 */

// Map to store pending requests
const pendingRequests = new Map();

/**
 * Deduplicate API requests
 * If a request with the same key is already in progress, returns the existing promise
 * Otherwise, makes a new request and caches the promise
 * 
 * @param {string} key - Unique key for the request (e.g., 'jobs_all', 'stats_summary')
 * @param {Function} requestFn - Function that returns a Promise (the actual API call)
 * @returns {Promise} - The request promise (either new or existing)
 * 
 * @example
 * const fetchJobs = () => deduplicateRequest('jobs_all', () => axios.get('/api/jobs/get_jobs'));
 */
export const deduplicateRequest = async (key, requestFn) => {
  // Check if a request with this key is already in progress
  if (pendingRequests.has(key)) {
    console.log(`[DEDUP] Reusing existing request for: ${key}`);
    const existingPromise = pendingRequests.get(key);
    // Clone the response if it's a fetch Response object
    return existingPromise.then((response) => {
      // If it's a fetch Response, clone it so each caller can read the body independently
      if (response instanceof Response) {
        return response.clone();
      }
      // For axios responses or other types, return as-is (they're already serialized)
      return response;
    });
  }

  // Create new request
  console.log(`[DEDUP] Starting new request for: ${key}`);
  const promise = requestFn()
    .then((response) => {
      // Request completed successfully, remove from pending
      pendingRequests.delete(key);
      return response;
    })
    .catch((error) => {
      // Request failed, remove from pending
      pendingRequests.delete(key);
      throw error;
    });

  // Store the promise in the map
  pendingRequests.set(key, promise);

  return promise;
};

/**
 * Generate a cache key from request parameters
 * Useful for creating unique keys for requests with different parameters
 * 
 * @param {string} endpoint - API endpoint
 * @param {Object} params - Request parameters
 * @returns {string} - Unique cache key
 * 
 * @example
 * const key = generateRequestKey('/api/jobs/get_jobs', { limit: 50, status: 'active' });
 * // Returns: '/api/jobs/get_jobs?limit=50&status=active'
 */
export const generateRequestKey = (endpoint, params = {}) => {
  if (!params || Object.keys(params).length === 0) {
    return endpoint;
  }

  // Sort params to ensure consistent keys
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}=${params[key]}`)
    .join('&');

  return `${endpoint}?${sortedParams}`;
};

/**
 * Clear a specific pending request (useful for manual cleanup)
 * @param {string} key - Request key to clear
 */
export const clearPendingRequest = (key) => {
  if (pendingRequests.has(key)) {
    pendingRequests.delete(key);
    console.log(`[DEDUP] Cleared pending request: ${key}`);
  }
};

/**
 * Clear all pending requests (useful for cleanup on logout or error)
 */
export const clearAllPendingRequests = () => {
  const count = pendingRequests.size;
  pendingRequests.clear();
  console.log(`[DEDUP] Cleared ${count} pending requests`);
};

/**
 * Get count of pending requests (useful for debugging)
 * @returns {number} - Number of pending requests
 */
export const getPendingRequestCount = () => {
  return pendingRequests.size;
};

export default deduplicateRequest;
